import type {
  KitchenMasterStateModel,
  KitchenDescriptionDetailModel,
  KitchenScheduleSettingsModel,
} from '@/model/kitchen';
import type { ApiResult } from '@/utils/api';
import axiosInstance from '@/utils/axios-instance';
import { isValidKitchenAdvanceMinutes } from '@/service/kitchenSchedule';

export const MASTER_TIMEOUT = 60000;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseDescription = (description?: string | null): Record<string, unknown> => {
  if (!description) return {};
  try {
    const value: unknown = JSON.parse(description);
    if (isRecord(value)) return value;
  } catch (error) {
    // 历史厨房描述允许纯文本，转换为 JSON 时保留完整原文。
    if (!(error instanceof SyntaxError)) throw error;
  }
  return { text: description };
};

export const readMasterFromDescription = (
  description: string | null | undefined,
  shopId: number,
): KitchenMasterStateModel | null => {
  const data = parseDescription(description);
  const masters = data.chefMasters;
  if (masters === undefined) return null;
  if (!isRecord(masters)) throw new Error('暂时无法读取主屏信息，请联系负责人协助处理');
  const state = masters[String(shopId)];
  if (state === undefined) return null;
  if (
    !isRecord(state) ||
    typeof state.account !== 'string' ||
    typeof state.accountName !== 'string' ||
    typeof state.deviceId !== 'string' ||
    typeof state.sessionId !== 'string' ||
    typeof state.enabled !== 'boolean' ||
    typeof state.heartbeatAt !== 'number' ||
    !Number.isFinite(state.heartbeatAt)
  ) {
    throw new Error('暂时无法读取主屏信息，请联系负责人协助处理');
  }
  return {
    account: state.account,
    accountName: state.accountName,
    deviceId: state.deviceId,
    sessionId: state.sessionId,
    enabled: state.enabled,
    heartbeatAt: state.heartbeatAt,
    takeoverAt: typeof state.takeoverAt === 'number' ? state.takeoverAt : undefined,
  };
};

export const isMasterOnline = (state: KitchenMasterStateModel | null, now = Date.now()): boolean =>
  Boolean(
    state?.enabled &&
      now - state.heartbeatAt < MASTER_TIMEOUT &&
      state.heartbeatAt - now < MASTER_TIMEOUT,
  );

export const isSameMaster = (
  current: KitchenMasterStateModel | null,
  expected: KitchenMasterStateModel,
): boolean =>
  current?.account === expected.account &&
  current?.deviceId === expected.deviceId &&
  current?.sessionId === expected.sessionId;

export const describeMasterLocation = (
  current: KitchenMasterStateModel,
  expected: KitchenMasterStateModel,
): string => {
  const name = current.accountName || current.account;
  if (isSameMaster(current, expected)) return `当前页面（${name}）`;
  if (current.deviceId === expected.deviceId) return `本设备的另一个页面（${name}）`;
  return current.account === expected.account ? `你的另一台设备（${name}）` : `其它设备（${name}）`;
};

// 同页面的设置保存和主屏心跳串行读改写，避免互相覆盖描述扩展字段。
const descriptionWrites = new Map<number, Promise<unknown>>();
const withDescriptionWrite = async <T>(kitchenId: number, action: () => Promise<T>): Promise<T> => {
  const previous = descriptionWrites.get(kitchenId) || Promise.resolve();
  const pending = previous.catch(() => undefined).then(action);
  descriptionWrites.set(kitchenId, pending);
  try {
    return await pending;
  } finally {
    if (descriptionWrites.get(kitchenId) === pending) descriptionWrites.delete(kitchenId);
  }
};

const readDescription = async (kitchenId: number): Promise<Record<string, unknown>> => {
  const { data } = await axiosInstance.post<ApiResult<KitchenDescriptionDetailModel>>(
    '/dim/kitchen/selectOne',
    { kitchenId },
    { timeout: 8000 },
  );
  if (data.code !== 200) throw new Error(data.msg || '暂时无法读取主屏信息，请刷新重试');
  return parseDescription(data.result?.description);
};

export const fetchKitchenMaster = async (
  kitchenId: number,
  shopId: number,
): Promise<KitchenMasterStateModel | null> =>
  readMasterFromDescription(JSON.stringify(await readDescription(kitchenId)), shopId);

export const saveKitchenMaster = async (
  kitchenId: number,
  shopId: number,
  state: KitchenMasterStateModel,
  action: 'acquire' | 'force' | 'heartbeat' | 'release',
): Promise<KitchenMasterStateModel | null> =>
  withDescriptionWrite(kitchenId, async () => {
    const description = await readDescription(kitchenId);
    const current = readMasterFromDescription(JSON.stringify(description), shopId);
    if (action === 'heartbeat' || action === 'release') {
      // 被接管的设备不能再写心跳，也不能把新主屏标记为关闭。
      if (!current?.enabled || !isSameMaster(current, state)) return current;
      if (action === 'heartbeat' && !isMasterOnline(current)) return current;
    } else if (action === 'acquire' && isMasterOnline(current) && !isSameMaster(current, state)) {
      if (current) {
        throw new Error(
          `${describeMasterLocation(
            current,
            state,
          )}正在作为主屏运行。如需改用当前页面，请使用“强制接管主屏”。`,
        );
      }
    }
    const masters = isRecord(description.chefMasters) ? description.chefMasters : {};
    const nextState = {
      ...(action === 'heartbeat' || action === 'release' ? current : {}),
      ...state,
      heartbeatAt: Date.now(),
    };
    const { data } = await axiosInstance.post<ApiResult<{ kitchenId: number }>>(
      '/dim/kitchen/edit',
      {
        kitchenId,
        // 厨房可能关联多个餐厅，各餐厅状态独立保存，同时保留原描述及其它数据。
        description: JSON.stringify({
          ...description,
          chefMasters: { ...masters, [String(shopId)]: nextState },
        }),
      },
      { timeout: 8000 },
    );
    if (data.code !== 200) throw new Error(data.msg || '主屏设置未能保存，请刷新查看后重试');
    return fetchKitchenMaster(kitchenId, shopId);
  });

const scheduleFromDescription = (
  description: Record<string, unknown>,
  shopId: number,
): KitchenScheduleSettingsModel => {
  const schedules = description.chefSchedules;
  if (schedules === undefined) return { advanceMinutes: 30 };
  if (!isRecord(schedules)) throw new Error('预约制作设置无效，请联系负责人');
  const settings = schedules[String(shopId)];
  if (settings === undefined) return { advanceMinutes: 30 };
  if (!isRecord(settings) || !isValidKitchenAdvanceMinutes(settings.advanceMinutes))
    throw new Error('预约制作设置无效，请联系负责人');
  return { advanceMinutes: settings.advanceMinutes };
};

export const fetchKitchenScheduleSettings = async (
  kitchenId: number,
  shopId: number,
): Promise<KitchenScheduleSettingsModel> =>
  scheduleFromDescription(await readDescription(kitchenId), shopId);

export const saveKitchenScheduleSettings = async (
  kitchenId: number,
  shopId: number,
  settings: KitchenScheduleSettingsModel,
  verifyPermission: () => Promise<boolean>,
): Promise<KitchenScheduleSettingsModel> => {
  if (!isValidKitchenAdvanceMinutes(settings.advanceMinutes))
    throw new Error('预约提前制作分钟数须为非负整数');
  if (!(await verifyPermission())) throw new Error('当前无法修改预约制作设置，请刷新查看');
  return withDescriptionWrite(kitchenId, async () => {
    const description = await readDescription(kitchenId);
    const schedules = isRecord(description.chefSchedules) ? description.chefSchedules : {};
    const { data } = await axiosInstance.post<ApiResult<{ kitchenId: number }>>(
      '/dim/kitchen/edit',
      {
        kitchenId,
        description: JSON.stringify({
          ...description,
          chefSchedules: { ...schedules, [String(shopId)]: settings },
        }),
      },
      { timeout: 8000 },
    );
    if (data.code !== 200) throw new Error(data.msg || '预约制作设置保存失败');
    const saved = await fetchKitchenScheduleSettings(kitchenId, shopId);
    if (saved.advanceMinutes !== settings.advanceMinutes)
      throw new Error('暂未确认预约制作设置保存结果，请刷新核对');
    return saved;
  });
};
